import React, { useEffect, useState } from "react";
import { useApp } from "@/lib/store";
import api from "@/lib/api";
import { Loader2, Save, RefreshCw, Trash2, Plus, Info, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const PLATFORM_KEYWORDS = [
    "lawsuit", "malpractice", "harassment", "HIPAA", "injury",
    "negligence", "privacy", "sued", "legal action",
];

export default function ReplyProtocol() {
    const { currentBusiness } = useApp();
    const [proto, setProto] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [regen, setRegen] = useState(false);
    const [draftProhibited, setDraftProhibited] = useState("");
    const [draftKeyword, setDraftKeyword] = useState("");

    const load = async () => {
        if (!currentBusiness) return;
        setLoading(true);
        try {
            const { protocol } = await api.reviewProtocol(currentBusiness.id);
            setProto(protocol);
        } catch {
            toast.error("Couldn't load protocol.");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentBusiness?.id]);

    const save = async () => {
        setSaving(true);
        try {
            const { protocol } = await api.updateReviewProtocol(currentBusiness.id, {
                tone: proto.tone,
                reply_length: proto.reply_length,
                contact_phone: proto.contact_phone,
                escalation_rules: proto.escalation_rules,
                autopost_ratings: proto.autopost_ratings,
                approval_required_ratings: proto.approval_required_ratings,
                user_prohibited_words: proto.user_prohibited_words,
                user_high_risk_keywords: proto.user_high_risk_keywords,
                per_rating_playbook: proto.per_rating_playbook,
                confirmed_by_user: true,
            });
            setProto(protocol);
            toast.success("Protocol saved.");
        } catch {
            toast.error("Save failed.");
        } finally {
            setSaving(false);
        }
    };

    const regenerate = async () => {
        setRegen(true);
        try {
            const { protocol } = await api.regenReviewProtocol(currentBusiness.id);
            setProto(protocol);
            toast.success("Fresh AI protocol generated.");
        } catch {
            toast.error("Regeneration failed.");
        } finally {
            setRegen(false);
        }
    };

    if (loading || !proto)
        return (
            <div className="py-16 text-center text-slate-500">
                <Loader2 className="h-6 w-6 animate-spin mx-auto" />
            </div>
        );

    const toggleRating = (list, r) => {
        const set = new Set(list);
        if (set.has(r)) set.delete(r);
        else set.add(r);
        return Array.from(set).sort();
    };

    return (
        <div className="space-y-5 fade-in" data-testid="reply-protocol-panel">
            <div className="flex items-center justify-between">
                <div>
                    <div className="label-eyebrow text-indigo-300">Reply Protocol</div>
                    <h2 className="text-2xl font-bold text-slate-100 tracking-tight">
                        How the AI replies to your reviews
                    </h2>
                    {!proto.confirmed_by_user && (
                        <Badge className="mt-2 bg-amber-500/15 border border-amber-500/30 text-amber-200">
                            AI-generated · please review &amp; confirm
                        </Badge>
                    )}
                </div>
                <Button
                    onClick={regenerate}
                    disabled={regen}
                    data-testid="protocol-regenerate-button"
                    variant="outline"
                    className="border-white/10 bg-white/[0.03] hover:bg-white/[0.08] gap-1.5"
                >
                    {regen ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                    Regenerate with AI
                </Button>
            </div>

            <div className="surface rounded-2xl p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <Label className="text-xs text-slate-400">Tone</Label>
                    <Input
                        data-testid="protocol-tone-input"
                        value={proto.tone || ""}
                        onChange={(e) => setProto({ ...proto, tone: e.target.value })}
                        className="mt-1.5 bg-white/[0.03] border-white/10 text-slate-100"
                    />
                </div>
                <div>
                    <Label className="text-xs text-slate-400">Reply length</Label>
                    <Select
                        value={proto.reply_length || "medium"}
                        onValueChange={(v) => setProto({ ...proto, reply_length: v })}
                    >
                        <SelectTrigger
                            data-testid="protocol-length-select"
                            className="mt-1.5 bg-white/[0.03] border-white/10 text-slate-100"
                        >
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="short">Short (1-2 sentences)</SelectItem>
                            <SelectItem value="medium">Medium (2-4 sentences)</SelectItem>
                            <SelectItem value="long">Long (4-6 sentences)</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <div>
                    <Label className="text-xs text-slate-400">Contact phone</Label>
                    <Input
                        data-testid="protocol-phone-input"
                        value={proto.contact_phone || ""}
                        onChange={(e) => setProto({ ...proto, contact_phone: e.target.value })}
                        className="mt-1.5 bg-white/[0.03] border-white/10 text-slate-100"
                    />
                </div>
                <div className="md:col-span-1">
                    <Label className="text-xs text-slate-400">Escalation rules</Label>
                    <Textarea
                        data-testid="protocol-escalation-input"
                        value={proto.escalation_rules || ""}
                        onChange={(e) => setProto({ ...proto, escalation_rules: e.target.value })}
                        rows={2}
                        className="mt-1.5 bg-white/[0.03] border-white/10 text-slate-100 text-sm"
                    />
                </div>
            </div>

            <div className="surface rounded-2xl p-5 space-y-4">
                <div>
                    <div className="label-eyebrow text-emerald-300 mb-2">Autopilot-eligible ratings</div>
                    <div className="flex items-center gap-2">
                        {[5, 4, 3, 2, 1].map((r) => (
                            <button
                                key={r}
                                data-testid={`protocol-autopost-${r}`}
                                onClick={() =>
                                    setProto({
                                        ...proto,
                                        autopost_ratings: toggleRating(proto.autopost_ratings || [], r),
                                    })
                                }
                                className={`px-3 py-1.5 rounded-full text-sm font-semibold border transition-colors ${
                                    (proto.autopost_ratings || []).includes(r)
                                        ? "bg-emerald-500/20 border-emerald-500/40 text-emerald-200"
                                        : "bg-white/[0.03] border-white/10 text-slate-500 hover:text-slate-200"
                                }`}
                            >
                                {r}★
                            </button>
                        ))}
                    </div>
                </div>
                <div>
                    <div className="label-eyebrow text-amber-300 mb-2">Approval-required ratings</div>
                    <div className="flex items-center gap-2">
                        {[5, 4, 3, 2, 1].map((r) => (
                            <button
                                key={r}
                                data-testid={`protocol-approval-${r}`}
                                onClick={() =>
                                    setProto({
                                        ...proto,
                                        approval_required_ratings: toggleRating(proto.approval_required_ratings || [], r),
                                    })
                                }
                                className={`px-3 py-1.5 rounded-full text-sm font-semibold border transition-colors ${
                                    (proto.approval_required_ratings || []).includes(r)
                                        ? "bg-amber-500/20 border-amber-500/40 text-amber-200"
                                        : "bg-white/[0.03] border-white/10 text-slate-500 hover:text-slate-200"
                                }`}
                            >
                                {r}★
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            <div className="surface rounded-2xl p-5">
                <div className="label-eyebrow text-slate-400 mb-3">Per-rating playbook</div>
                <div className="space-y-2">
                    {[5, 4, 3, 2, 1].map((r) => (
                        <div key={r} className="flex items-start gap-3">
                            <div className="w-8 shrink-0 text-sm font-bold text-slate-300">{r}★</div>
                            <Textarea
                                data-testid={`protocol-playbook-${r}`}
                                rows={2}
                                value={proto.per_rating_playbook?.[String(r)] || ""}
                                onChange={(e) =>
                                    setProto({
                                        ...proto,
                                        per_rating_playbook: {
                                            ...(proto.per_rating_playbook || {}),
                                            [String(r)]: e.target.value,
                                        },
                                    })
                                }
                                className="flex-1 bg-white/[0.03] border-white/10 text-slate-100 text-sm"
                            />
                        </div>
                    ))}
                </div>
            </div>

            <ClaimsEditor
                title="Your prohibited words (blocked in generated replies)"
                testid="protocol-prohibited"
                items={proto.user_prohibited_words || []}
                onChange={(v) => setProto({ ...proto, user_prohibited_words: v })}
                color="rose"
                draft={draftProhibited}
                setDraft={setDraftProhibited}
            />

            <ClaimsEditor
                title="Your extra high-risk keywords (force human approval)"
                testid="protocol-highrisk"
                items={proto.user_high_risk_keywords || []}
                onChange={(v) => setProto({ ...proto, user_high_risk_keywords: v })}
                color="amber"
                draft={draftKeyword}
                setDraft={setDraftKeyword}
            />

            <div className="surface rounded-2xl p-4 border-l-2 border-l-rose-500/60 flex items-start gap-2">
                <ShieldCheck className="h-4 w-4 text-rose-300 mt-0.5" />
                <div className="flex-1">
                    <div className="text-xs font-semibold text-rose-200">
                        Platform safety keywords (always enforced — not editable)
                    </div>
                    <div
                        data-testid="protocol-platform-keywords"
                        className="mt-2 flex flex-wrap gap-1.5"
                    >
                        {PLATFORM_KEYWORDS.map((k) => (
                            <span
                                key={k}
                                className="text-[11px] font-mono uppercase tracking-widest bg-rose-500/10 border border-rose-500/30 text-rose-200 px-2 py-0.5 rounded-full"
                            >
                                {k}
                            </span>
                        ))}
                    </div>
                </div>
            </div>

            <div className="flex justify-end pt-2 border-t border-white/5">
                <Button
                    onClick={save}
                    disabled={saving}
                    data-testid="protocol-save-button"
                    className="bg-indigo-500 hover:bg-indigo-400 text-white gap-1.5"
                >
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                    Save protocol
                </Button>
            </div>
        </div>
    );
}

function ClaimsEditor({ title, testid, items, onChange, color, draft, setDraft }) {
    const colors = {
        rose: "bg-rose-500/10 border-rose-500/30 text-rose-200",
        amber: "bg-amber-500/10 border-amber-500/30 text-amber-200",
    };
    return (
        <div className="surface rounded-2xl p-5">
            <Label className="text-xs text-slate-400">{title}</Label>
            <div className="mt-2 flex flex-wrap gap-2" data-testid={`${testid}-list`}>
                {items.map((c, i) => (
                    <div
                        key={i}
                        data-testid={`${testid}-item-${i}`}
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs ${colors[color]}`}
                    >
                        {c}
                        <button
                            onClick={() => onChange(items.filter((_, j) => j !== i))}
                            data-testid={`${testid}-remove-${i}`}
                        >
                            <Trash2 className="h-3 w-3 opacity-60 hover:opacity-100" />
                        </button>
                    </div>
                ))}
            </div>
            <div className="mt-3 flex items-center gap-2">
                <Input
                    data-testid={`${testid}-input`}
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    placeholder="Add…"
                    className="bg-white/[0.03] border-white/10 text-slate-100 h-9 text-sm"
                    onKeyDown={(e) => {
                        if (e.key === "Enter" && draft.trim()) {
                            e.preventDefault();
                            onChange([...items, draft.trim()]);
                            setDraft("");
                        }
                    }}
                />
                <Button
                    variant="outline"
                    data-testid={`${testid}-add-button`}
                    onClick={() => {
                        if (draft.trim()) {
                            onChange([...items, draft.trim()]);
                            setDraft("");
                        }
                    }}
                    className="border-white/10 bg-white/5 hover:bg-white/10 text-slate-100 gap-1.5 h-9"
                >
                    <Plus className="h-3.5 w-3.5" /> Add
                </Button>
            </div>
        </div>
    );
}
